/**
 * StandHallView — Stand Krokisi bileşeni
 * CustomerDashboard ve BlueZoneLayoutEdit sayfalarında kullanılır.
 *
 * Props:
 *   layout  - my-layout / hall-view API'sinin döndürdüğü layout nesnesi
 *   maxH    - SVG max yüksekliği (px, default 480)
 *   compact - daha küçük padding (boolean, default false)
 */
import React from "react";

const CLOSED_W = 9;
const OPEN_W   = 3;
const HALO_CLR = "rgba(255,255,255,0.9)";
const FONT_CLR = "#0f172a";

function hexToRgba(hex, alpha) {
  if (!hex || hex.length < 7) return `rgba(148,163,184,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function StandHallView({ layout, maxH = 480, compact = false }) {
  if (!layout) return null;

  const w = layout.base_width  || 1;
  const h = layout.base_height || 1;

  const rawColor     = layout.hall_stand_color     || "#f59e0b";
  const transparency = (layout.hall_stand_transparency ?? 70) / 100;
  const statusColor  = hexToRgba(rawColor, transparency);
  const adjStands    = layout.adjacent_stands || [];
  const openSides    = layout.open_sides      || [];

  // Toplam bounding box
  let totalMinX = 0, totalMinY = 0;
  let totalMaxX = w, totalMaxY = h;
  adjStands.forEach((adj) => {
    totalMinX = Math.min(totalMinX, adj.rel_x);
    totalMinY = Math.min(totalMinY, adj.rel_y);
    totalMaxX = Math.max(totalMaxX, adj.rel_x + adj.width);
    totalMaxY = Math.max(totalMaxY, adj.rel_y + adj.height);
  });
  const totalCols = totalMaxX - totalMinX;
  const totalRows = totalMaxY - totalMinY;

  const PAD = compact ? 48 : 64;
  const maxSvgW = 560;
  const cellSize = Math.min(
    Math.floor(maxSvgW / totalCols),
    Math.floor(maxH     / totalRows),
    compact ? 44 : 56
  );

  const offsetX = -totalMinX;
  const offsetY = -totalMinY;
  const svgW = totalCols * cellSize + PAD * 2;
  const svgH = totalRows * cellSize + PAD * 2;
  const standX = PAD + offsetX * cellSize;
  const standY = PAD + offsetY * cellSize;

  const standFontSz = Math.max(10, cellSize * 0.55);
  const nameFontSz  = Math.max(13, standFontSz * 1.4);

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ width: "100%", maxHeight: `${maxH}px`, shapeRendering: "crispEdges" }}
    >
      {/* ── Komşu standlar ── */}
      {adjStands.map((adj, i) => {
        const ax = PAD + (offsetX + adj.rel_x) * cellSize;
        const ay = PAD + (offsetY + adj.rel_y) * cellSize;
        const aw = adj.width  * cellSize;
        const ah = adj.height * cellSize;
        const adjFontSz = Math.max(9, cellSize * 0.38);
        return (
          <g key={`adj-${i}`}>
            <rect x={ax} y={ay} width={aw} height={ah} fill="#e2e8f0" />
            <rect x={ax} y={ay} width={aw} height={ah} fill="rgba(148,163,184,0.25)" />
            {Array.from({ length: adj.width + 1 }).map((_, ci) => (
              <line key={`acv-${i}-${ci}`}
                x1={ax + ci * cellSize} y1={ay}
                x2={ax + ci * cellSize} y2={ay + ah}
                stroke="#cbd5e1" strokeWidth="0.5" />
            ))}
            {Array.from({ length: adj.height + 1 }).map((_, ri) => (
              <line key={`ach-${i}-${ri}`}
                x1={ax} y1={ay + ri * cellSize}
                x2={ax + aw} y2={ay + ri * cellSize}
                stroke="#cbd5e1" strokeWidth="0.5" />
            ))}
            <rect x={ax} y={ay} width={aw} height={ah} fill="none" stroke="#94a3b8" strokeWidth="1" />
            {adj.stand_no && (
              <text x={ax + aw / 2} y={ay + ah / 2}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={adjFontSz} fontWeight="600" fill="#64748b" fontFamily="monospace"
                stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" paintOrder="stroke">
                {adj.stand_no}
              </text>
            )}
          </g>
        );
      })}

      {/* ── Ana stand zemin ── */}
      <rect x={standX} y={standY} width={w * cellSize} height={h * cellSize} fill="#f8fafc" />
      <rect x={standX} y={standY} width={w * cellSize} height={h * cellSize} fill={statusColor} />

      {Array.from({ length: w + 1 }).map((_, i) => (
        <line key={`vg-${i}`}
          x1={standX + i * cellSize} y1={standY}
          x2={standX + i * cellSize} y2={standY + h * cellSize}
          stroke="#cbd5e1" strokeWidth="0.5" />
      ))}
      {Array.from({ length: h + 1 }).map((_, i) => (
        <line key={`hg-${i}`}
          x1={standX} y1={standY + i * cellSize}
          x2={standX + w * cellSize} y2={standY + i * cellSize}
          stroke="#cbd5e1" strokeWidth="0.5" />
      ))}
      <rect x={standX} y={standY} width={w * cellSize} height={h * cellSize}
        fill="none" stroke="#94a3b8" strokeWidth="1.5" />

      {/* ── Açık/Kapalı kenar çizgileri ── */}
      <line x1={standX} y1={standY} x2={standX + w * cellSize} y2={standY}
        stroke={openSides.includes("north") ? "#22c55e" : "#111827"}
        strokeWidth={openSides.includes("north") ? OPEN_W : CLOSED_W} />
      <line x1={standX + w * cellSize} y1={standY} x2={standX + w * cellSize} y2={standY + h * cellSize}
        stroke={openSides.includes("east") ? "#22c55e" : "#111827"}
        strokeWidth={openSides.includes("east") ? OPEN_W : CLOSED_W} />
      <line x1={standX} y1={standY + h * cellSize} x2={standX + w * cellSize} y2={standY + h * cellSize}
        stroke={openSides.includes("south") ? "#22c55e" : "#111827"}
        strokeWidth={openSides.includes("south") ? OPEN_W : CLOSED_W} />
      <line x1={standX} y1={standY} x2={standX} y2={standY + h * cellSize}
        stroke={openSides.includes("west") ? "#22c55e" : "#111827"}
        strokeWidth={openSides.includes("west") ? OPEN_W : CLOSED_W} />

      {/* ── Stand No ── */}
      {layout.stand_no && (
        <text x={standX + w * cellSize - 5} y={standY + standFontSz + 3}
          textAnchor="end" fontSize={standFontSz} fontWeight="700" fill={FONT_CLR}
          fontFamily="monospace" stroke={HALO_CLR} strokeWidth={standFontSz * 0.2}
          paintOrder="stroke" opacity="0.95">
          {layout.stand_no}
        </text>
      )}

      {/* ── Ülke adı ── */}
      {layout.country && (
        <text x={standX + (w * cellSize) / 2} y={standY + (h * cellSize) / 2}
          textAnchor="middle" dominantBaseline="middle"
          fontSize={nameFontSz} fontWeight="800" fill={FONT_CLR}
          fontFamily="sans-serif" stroke={HALO_CLR} strokeWidth={nameFontSz * 0.2}
          paintOrder="stroke">
          {layout.country}
        </text>
      )}

      {/* ── Alan (m²) ── */}
      <text x={standX + (w * cellSize) / 2} y={standY + h * cellSize - 6}
        textAnchor="middle" fontSize={standFontSz * 0.85}
        fill={FONT_CLR} fontFamily="sans-serif" opacity="0.75"
        stroke={HALO_CLR} strokeWidth={standFontSz * 0.16} paintOrder="stroke">
        {layout.total_area}m²
      </text>

      {/* ── A/B/C/D kenar etiketleri ── */}
      {[
        { label: "A", x: standX + (w * cellSize) / 2,  y: standY - 26,              fill: "#ef4444" },
        { label: "B", x: standX + w * cellSize + 26,   y: standY + (h * cellSize) / 2, fill: "#f59e0b" },
        { label: "C", x: standX + (w * cellSize) / 2,  y: standY + h * cellSize + 26,  fill: "#8b5cf6" },
        { label: "D", x: standX - 26,                  y: standY + (h * cellSize) / 2, fill: "#10b981" },
      ].map(({ label, x, y, fill }) => (
        <g key={label}>
          <circle cx={x} cy={y} r="18" fill={fill} />
          <text x={x} y={y + 5} textAnchor="middle" dominantBaseline="middle"
            fontSize="14" fontWeight="bold" fill="white">
            {label}
          </text>
        </g>
      ))}
    </svg>
  );
}
